import { CommonModule } from '@angular/common';
import {
  Component,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { register } from 'swiper/element/bundle';

import { CarouselData, CarouselSettings } from '../interfaces/carousel';
import type Swiper from 'swiper';

@Component({
  selector: 'digital-platform-carousel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CarouselComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input() data: CarouselData[] = [];
  @Input() settings: CarouselSettings = {
    autoplay: false,
    autoplayInterval: 7000,
    fade: false,
    loop: false,
    fadeSpeed: 1000,
    pagination: true,
    loadFromCMS: false,
  };

  @ViewChild('swiperRef', { static: false })
  swiperRef!: ElementRef<HTMLElement>;

  slides: CarouselData[] = [];
  activeIndex = 0;

  // Instead of `any`, use the actual Swiper type and number for setInterval
  private swiperInstance?: Swiper;
  private autoplayIntervalRef?: number;
  private translatePipe = inject(TranslatePipe);

  constructor(private cd: ChangeDetectorRef) {
    register();
  }

  ngOnInit(): void {
    this.initializeSlides();
  }

  ngAfterViewInit(): void {
    // Cast nativeElement to HTMLElement + a `swiper: Swiper` property
    const swiperEl = this.swiperRef.nativeElement as HTMLElement & {
      swiper: Swiper;
    };

    // Set initial attributes
    swiperEl.setAttribute('effect', this.settings.fade ? 'fade' : 'slide');
    swiperEl.setAttribute('speed', String(this.settings.fadeSpeed));
    swiperEl.setAttribute('loop', String(this.settings.loop));

    // Grab the Swiper instance off the custom element
    this.swiperInstance = swiperEl.swiper;

    if (this.swiperInstance) {
      this.activeIndex = this.swiperInstance.realIndex;
      // Use a typed event listener; onSlideChange expects a CustomEvent<Swiper[]>
      swiperEl.addEventListener(
        'slidechange',
        this.onSlideChange.bind(this) as EventListener
      );

      if (this.settings.autoplay) {
        this.startAutoplay();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['settings']) {
      // 1. Recompute slides
      this.initializeSlides();

      // 2. Trigger change detection so *ngFor can render new slides
      this.cd.detectChanges();

      // 3. If Swiper exists already, update its attributes and layout
      if (this.swiperInstance) {
        const swiperEl = this.swiperRef.nativeElement as HTMLElement & {
          swiper: Swiper;
        };

        swiperEl.setAttribute('effect', this.settings.fade ? 'fade' : 'slide');
        swiperEl.setAttribute('speed', String(this.settings.fadeSpeed));
        swiperEl.setAttribute('loop', String(this.settings.loop));

        if (this.settings.autoplay) {
          this.startAutoplay();
        } else if (this.autoplayIntervalRef !== undefined) {
          clearInterval(this.autoplayIntervalRef);
        }

        this.swiperInstance.update();
      }
    }
  }

  initializeSlides(): void {
    if (this.settings.loadFromCMS) {
      const prefix = this.settings.cmsContentKey ?? 'PLAYGROUND.TEST.CAROUSEL';
      const numberOfSlides = this.settings.numberOfSlides ?? 3;

      this.slides = Array.from({ length: numberOfSlides }, (_, i) => ({
        image: this.translatePipe.transform(`${prefix}.IMAGE_${i + 1}`),
        title: this.translatePipe.transform(`${prefix}.TITLE_${i + 1}`),
        description: this.translatePipe.transform(
          `${prefix}.DESCRIPTION_${i + 1}`
        ),
      }));
    } else {
      this.slides = this.data || [];
    }
  }

  private startAutoplay(): void {
    if (this.autoplayIntervalRef !== undefined) {
      clearInterval(this.autoplayIntervalRef);
    }

    // window.setInterval returns a number in a browser environment
    this.autoplayIntervalRef = window.setInterval(() => {
      if (!this.swiperInstance) {
        return;
      }

      const current = this.swiperInstance.realIndex;
      const lastIndex = this.slides.length - 1;
      const nextIndex =
        current < lastIndex ? current + 1 : this.settings.loop ? 0 : current;

      if (current !== nextIndex) {
        this.swiperInstance.slideTo(nextIndex);
      }
    }, this.settings.autoplayInterval);
  }

  // Use CustomEvent<Swiper[]> so that event.detail is strongly typed
  onSlideChange(event: Event): void {
    const [swiper] = (event as CustomEvent<Swiper[]>).detail;
    this.activeIndex = swiper.realIndex;
    this.cd.detectChanges();
  }

  goToSlide(index: number): void {
    if (!this.swiperInstance) {
      return;
    }

    if (!this.settings.loop && index === this.activeIndex) {
      return;
    }

    this.swiperInstance.slideTo(index);

    if (this.settings.autoplay) {
      this.startAutoplay();
    }
  }

  ngOnDestroy(): void {
    if (this.swiperRef?.nativeElement) {
      const swiperEl = this.swiperRef.nativeElement as HTMLElement & {
        swiper: Swiper;
      };
      swiperEl.removeEventListener(
        'slidechange',
        this.onSlideChange.bind(this) as EventListener
      );
    }
    if (this.autoplayIntervalRef !== undefined) {
      clearInterval(this.autoplayIntervalRef);
    }
  }
}
